import * as THREE from 'three';
import { worldNumberOfLevel } from '../content/campaign';
import { DT } from '../sim/constants';
import { DRONE_SLOTS, FEEL, HULL_GUNS } from '../sim/FeelConstants';
import { Fam, PbMode } from '../sim/pools/BulletPool';
import { isPowerup, PickupKind } from '../sim/pools/PickupPool';
import type { World } from '../sim/World';
import { ARCHETYPE_INDEX, archetypeFor, buildAllMeshes } from './art/models';
import { Backdrop, SCROLL_SPEED } from './Backdrop';
import { HullFleet } from './batches/HullFleet';
import { PickupView } from './batches/PickupView';
import { QuadBatch } from './batches/QuadBatch';
import { BulletShape, styleFor } from './BulletStyle';
import { ObstacleView } from './ObstacleView';
import {
  BULLET_CORE,
  BULLET_RIM,
  ENEMY_BULLET,
  PLAYER_BULLET,
  PLAYER_DRONE,
  PLAYER_SECONDARY,
} from './palette';
import { Layer } from './RenderLayers';
import type { Stage } from './Stage';
import { worldGrade } from './WorldGrade';

/** Palettes réservées (PLAN.md §4.5). Ces trois teintes appartiennent AUX
 *  PROJECTILES ENNEMIS et à rien d'autre — ni décor, ni explosions, ni HUD.
 *  C'est ce qui permet au joueur d'apprendre en trente secondes que « rose ou
 *  rouge = ça tue », sans qu'on le lui dise jamais. */
const C_ENEMY = ENEMY_BULLET;
const C_PLAYER_BULLET = PLAYER_BULLET[0];
const C_PLAYER_SECONDARY = PLAYER_SECONDARY[0];
const C_PLAYER_DRONE = PLAYER_DRONE[0];

/** Teinte par TYPE de power-up (indexée par `PickupKind`). Toutes HORS de la
 *  palette danger (rouge/rose/violet) : un jeton ne doit jamais se confondre avec
 *  un projectile ennemi. Ambre=bombe, vert=vie, or=ulti, cyan=refroidissement,
 *  lime=suralimentation. La case `Cell` (0) n'est jamais rendue. */
const PICKUP_RGB: readonly (readonly [number, number, number])[] = [
  [1, 1, 1], // Cell — non rendu
  [1.0, 0.62, 0.16], // Bomb   — ambre
  [0.32, 1.0, 0.6], // Repair  — vert
  [1.0, 0.86, 0.3], // Surge   — or (comme la jauge d'ulti)
  [0.28, 0.82, 1.0], // Coolant — cyan
  [0.78, 1.0, 0.42], // Overdrive — lime
];
/** Diamètre visuel d'un jeton de power-up, en unités monde. Assez gros pour se
 *  remarquer au coin de l'œil, sans masquer le rideau de projectiles. */
const PICKUP_SIZE = 30;

const _mat = new THREE.Matrix4();
const _pos = new THREE.Vector3();
const _quat = new THREE.Quaternion();
const _scale = new THREE.Vector3();
/** Axe de roulis, alloué une fois : la vue n'alloue rien par frame. */
const _axisZ = new THREE.Vector3(0, 0, 1);

/** Teinte d'instance par RANG (popcorn / troupe / lourd).
 *
 *  ⚠️ Depuis le J13 ce n'est plus une COULEUR mais un MULTIPLICATEUR : la
 *  peinture vient de l'atlas de palette, et l'instance ne fait que la moduler.
 *  Le rang se lit donc comme une variation de valeur — le popcorn est plus
 *  pâle, le lourd plus sombre — ce qui reste vrai en vision daltonienne, alors
 *  que trois teintes ne l'étaient pas. */
const RANK_TINT = [1.16, 1.0, 0.82];

/** Rayon visuel du vaisseau joueur, en unités monde. Sans rapport avec la
 *  hitbox, qui vaut 9 : l'écart est l'assistance la plus importante du jeu et
 *  il est assumé (PLAN.md §6.2). Le point de hitbox, lui, est dessiné à sa
 *  taille réelle et toujours visible. */
const PLAYER_RADIUS = 34;
const DRONE_RADIUS = 12;

/**
 * Le miroir de la simulation.
 *
 * Sens unique : cette classe LIT le monde et ne lui écrit jamais
 * (PLAN.md §3.1). Elle ne prend aucune décision de jeu — pas même « cet ennemi
 * clignote parce qu'il vient d'être touché », qui se déduira des événements
 * drainés, jamais d'un champ ajouté dans la simulation.
 */
export class View {
  private readonly bullets: QuadBatch[];
  private readonly playerBullets: QuadBatch;
  private readonly secondaryBullets: QuadBatch;
  private readonly droneBullets: QuadBatch;
  /** Les POWER-UPS qui tombent (jetons lumineux). La monnaie `Cell` n'est pas
   *  rendue (son économie arrive au J7) — seuls les bonus se voient. */
  private readonly pickupsView: PickupView;
  private readonly fleet: HullFleet;
  /** Géométries du catalogue, construites une fois. Conservées pour pouvoir
   *  RE-tailler la coque du joueur quand il change de vaisseau (`setShip`). */
  private readonly built: ReturnType<typeof buildAllMeshes>;
  private readonly player: THREE.Mesh;
  /** Matériau PROPRE au joueur : un clone de celui de la flotte, pour pouvoir
   *  faire ROUGIR sa coque à la surchauffe du canon sans teinter tout le reste
   *  de la flotte, qui partage l'original. Même clé de cache (`hull-v1`), donc
   *  aucune variante de shader en plus au warm-up. */
  private readonly playerMat: THREE.MeshStandardMaterial;
  private readonly drones: THREE.InstancedMesh;
  private readonly hitDot: THREE.Mesh;
  private readonly grazeRing: THREE.Mesh;
  private readonly backdrop: Backdrop;
  private readonly obstacles: ObstacleView;
  /** Table `kind` numérique → archétype de rendu, construite à la PREMIÈRE
   *  synchronisation. Elle ne peut pas l'être au constructeur : le catalogue de
   *  définitions vit dans le monde, que la vue ne reçoit qu'à `sync`. Une seule
   *  allocation, jamais dans le régime permanent. */
  private kindToArchetype: Int32Array | null = null;

  constructor(
    stage: Stage,
    capEnemyBullets: number,
    capPlayerBullets: number,
    capEnemies: number,
    capPickups: number,
  ) {
    // Une InstancedMesh par famille : le nombre de draw calls des projectiles
    // est donc borné à quatre, indépendamment de la densité à l'écran.
    //
    // ⚠️ Les projectiles ENNEMIS vont dans la scène OVERLAY, les tirs du JOUEUR
    // dans la scène principale. Ce n'est pas une commodité d'organisation :
    // c'est ce qui fait que les balles qui tuent ne sont jamais bloomées,
    // jamais dégradées par le resolution scale, et d'une couleur exactement
    // conforme à la palette autorisée (PLAN.md §4.4).
    this.bullets = C_ENEMY.map(
      (c, i) =>
        new QuadBatch(
          { core: BULLET_CORE, body: c, rim: BULLET_RIM },
          capEnemyBullets,
          false,
          Layer.Danger + i,
        ),
    );
    for (const b of this.bullets) stage.overlay.add(b.mesh);

    this.playerBullets = new QuadBatch(
      { core: '#FFFFFF', body: C_PLAYER_BULLET, rim: '#0A2A33' },
      capPlayerBullets,
      true,
      Layer.PlayerBullets,
    );
    stage.scene.add(this.playerBullets.mesh);

    /* Le tir SECONDAIRE a son propre lot et sa propre teinte. Un seul lot
       partagé avec le primaire imposerait une couleur unique — c'est
       exactement ce qui rendait le clic droit invisible. Le coût est d'un
       draw call, à comparer au fait qu'une arme entière ne se voyait pas. */
    this.secondaryBullets = new QuadBatch(
      { core: '#FFFFFF', body: C_PLAYER_SECONDARY, rim: '#0B3324' },
      capPlayerBullets,
      true,
      Layer.PlayerBullets + 1,
    );
    stage.scene.add(this.secondaryBullets.mesh);

    /* Le tir des DRONES a son TROISIÈME lot, sur retour de jeu : « les drones
       tirent des balles aussi grosses que les miennes ». Un calibre d'appui doit
       se lire comme plus léger — plus petit, plus vif, cyan plus pâle. La TAILLE
       vient du rayon réel du projectile (voir `sync`), jamais d'une constante.

       ⚠️ Alloué UNE fois à la capacité maximale des tirs joueur, comme les deux
       autres : `count` varie, jamais l'allocation. Et il DOIT passer par
       `primeForWarmup`, sinon son programme — une teinte distincte, donc une
       variante de shader distincte — se compilerait à la première salve de
       drones, c'est-à-dire pendant la première vague. Le coût est d'un draw call
       et d'un programme, tous deux bornés et mesurés par le compteur de warm-up. */
    this.droneBullets = new QuadBatch(
      { core: '#FFFFFF', body: C_PLAYER_DRONE, rim: '#0A2A33' },
      capPlayerBullets,
      true,
      Layer.PlayerBullets + 2,
    );
    stage.scene.add(this.droneBullets.mesh);

    /* Les POWER-UPS qui tombent. Scène PRINCIPALE, à `Layer.Pickups` : ils
       BLOOMENT (un butin qui glow attire l'œil) et restent visibles au-dessus du
       décor. Un seul draw call, une couleur par TYPE via `instanceColor`. Doit
       passer par `primeForWarmup` — sinon son programme se compilerait au premier
       jeton lâché, en pleine vague. */
    this.pickupsView = new PickupView(capPickups, Layer.Pickups);
    stage.scene.add(this.pickupsView.mesh);

    /* ── La flotte ────────────────────────────────────────────────────────
       Vingt-deux silhouettes procédurales, un seul matériau, un seul programme
       de shader. Les cubes gris du J1 disparaissent ici : le critère de fin du
       J13 est « le jeu est reconnaissable sur une seule capture d'écran », et
       une primitive n'est reconnaissable de rien. */
    this.fleet = new HullFleet(stage.scene, capEnemies, Layer.Enemies);

    // Le décor partage le matériau de coques : le canyon et les vaisseaux sont
    // littéralement taillés par le même outil, ce qui est toute la thèse de la
    // direction artistique retenue.
    this.backdrop = new Backdrop(stage.scene, this.fleet.material);
    // Décor collisionnable : astéroïdes et complexes militaires. Vue autonome,
    // ses propres géométries/matériaux, alimentée depuis `w.obstacles`.
    this.obstacles = new ObstacleView(stage.scene);

    // Le joueur et les drones réutilisent les géométries du catalogue plutôt
    // que d'en fabriquer de nouvelles : une seule source de forme, donc aucune
    // dérive possible entre le vaisseau et le reste de la flotte.
    this.built = buildAllMeshes();

    /* Le joueur porte un CLONE du matériau de flotte. Le clone copie la
       référence de l'atlas (aucune VRAM en plus) et hérite de `onBeforeCompile`
       et de la clé de cache `hull-v1` — donc le programme est partagé avec la
       variante non-instanciée déjà présente, et le compte de warm-up ne bouge
       pas. On s'en sert pour piloter `emissive` (rouge de surchauffe) sur le
       seul vaisseau, sans toucher aux drones ni aux ennemis. */
    this.playerMat = this.fleet.material.clone() as THREE.MeshStandardMaterial;
    this.player = new THREE.Mesh(this.geoFor('player'), this.playerMat);
    this.player.scale.setScalar(PLAYER_RADIUS);
    this.player.renderOrder = Layer.Player;
    stage.scene.add(this.player);

    /* L'escorte. Un `InstancedMesh` alloué UNE fois à la capacité maximale des
       emplacements : `count` varie, jamais l'allocation. Un drone acquis en
       plein combat ne doit provoquer ni allocation ni compilation de shader.

       Teinte volontairement plus froide et plus sombre que le fuselage : les
       drones doivent se lire comme des satellites, pas comme huit vaisseaux
       parmi lesquels chercher lequel on pilote. */
    this.drones = new THREE.InstancedMesh(
      this.geoFor('drone'),
      this.fleet.material,
      DRONE_SLOTS.length,
    );
    this.drones.frustumCulled = false;
    this.drones.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    /* ⚠️ Une couleur d'instance neutre, posée alors qu'aucun code ne s'en sert.
       `USE_INSTANCING_COLOR` est un `#define` : un `InstancedMesh` qui en est
       dépourvu produit une VARIANTE DE PROGRAMME distincte du reste de la
       flotte, pour le même matériau. Trois octets de tampon achètent ici un
       programme de shader en moins à compiler au démarrage. */
    this.drones.instanceColor = new THREE.InstancedBufferAttribute(
      new Float32Array(DRONE_SLOTS.length * 3).fill(1),
      3,
    );
    this.drones.count = 0;
    this.drones.renderOrder = Layer.Player;
    stage.scene.add(this.drones);

    // Le point de hitbox est TOUJOURS visible (PLAN.md §6.2) — jamais une
    // option, jamais un déblocage. C'est ce qui rend un rideau franchissable
    // plutôt qu'arbitraire.
    this.hitDot = new THREE.Mesh(
      new THREE.CircleGeometry(1, 24),
      new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, depthTest: false }),
    );
    // Dans l'OVERLAY : le point de hitbox ne doit jamais être noyé par le bloom
    // ni adouci par le resolution scale. C'est l'élément le plus important de
    // l'écran (PLAN.md §6.2).
    this.hitDot.renderOrder = Layer.PlayerOverlay;
    stage.overlay.add(this.hitDot);

    this.grazeRing = new THREE.Mesh(
      new THREE.RingGeometry(0.92, 1, 32),
      new THREE.MeshBasicMaterial({
        color: 0x3fe8ff,
        transparent: true,
        opacity: 0.14,
        depthTest: false,
      }),
    );
    this.grazeRing.renderOrder = Layer.PlayerOverlay - 1;
    stage.overlay.add(this.grazeRing);
  }

  /**
   * Remplit chaque lot d'UNE instance hors champ, le temps du warm-up.
   *
   * Sans cela, tous les `InstancedMesh` ont `count = 0` au démarrage — le monde
   * vient d'être créé, aucun projectile n'existe encore — et le renderer les
   * ignore purement et simplement. Aucun programme n'est compilé, le warm-up
   * annonce un chiffre rassurant, et les shaders se compilent un par un pendant
   * la première vague : exactement le freeze qu'il devait supprimer.
   *
   * C'est le genre de panne qu'aucune relecture n'attrape et que seul le
   * compteur de programmes révèle. Il l'a révélée.
   */
  /** Géométrie fraîche pour un archétype du catalogue. Chaque appel alloue —
   *  réservé au montage et aux (rares) changements de vaisseau, jamais par frame. */
  private geoFor(id: string): THREE.BufferGeometry {
    const data = this.built[ARCHETYPE_INDEX.get(id) ?? 0]!;
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(data.position, 3));
    g.setAttribute('normal', new THREE.Float32BufferAttribute(data.normal, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(data.uv, 2));
    return g;
  }

  /**
   * Change la SILHOUETTE du vaisseau piloté selon l'identifiant de coque équipée.
   *
   * Retombe sur la coque de série (`player`) pour tout identifiant sans archétype
   * `ship-<id>` dédié : un vaisseau reste TOUJOURS visible, jamais un objet
   * manquant — la même règle de sûreté que `archetypeFor` côté ennemis. L'ancienne
   * géométrie est libérée : appelée à chaque démarrage de run, elle ne doit pas
   * fuir un tampon GPU à chaque partie.
   */
  setShip(shipId: string): void {
    const archId = ARCHETYPE_INDEX.has(`ship-${shipId}`) ? `ship-${shipId}` : 'player';
    const previous = this.player.geometry;
    this.player.geometry = this.geoFor(archId);
    previous.dispose();
  }

  primeForWarmup(): void {
    const FAR = 100000; // hors du frustum, mais présent dans le graphe
    for (const b of this.bullets) {
      b.begin();
      b.add(FAR, FAR, 2, 10, 1, 0);
      b.end();
    }
    this.playerBullets.begin();
    this.playerBullets.add(FAR, FAR, 1, 10, 1, 0);
    this.playerBullets.end();
    this.secondaryBullets.begin();
    this.secondaryBullets.add(FAR, FAR, 1, 10, 1, 0);
    this.secondaryBullets.end();
    this.droneBullets.begin();
    this.droneBullets.add(FAR, FAR, 1, 10, 1, 0);
    this.droneBullets.end();
    // Un jeton hors champ force la compilation du programme de power-up ici, et
    // non au premier bonus lâché (c'est-à-dire en pleine action).
    this.pickupsView.begin();
    this.pickupsView.add(FAR, FAR, 10, 0, 1, 1, 1);
    this.pickupsView.end();

    // Toute la flotte, y compris les contours de boss : les vingt-deux lots
    // partagent un matériau, mais le lot de contour, lui, en a un autre — et
    // c'est exactement le genre de variante qu'on oublie.
    this.fleet.primeForWarmup();
    this.obstacles.primeForWarmup();

    _pos.set(FAR, FAR, 0);
    _quat.identity();
    _scale.setScalar(1);
    _mat.compose(_pos, _quat, _scale);
    this.drones.setMatrixAt(0, _mat);
    this.drones.count = 1;
    this.drones.instanceMatrix.needsUpdate = true;

    this.player.visible = true;
    this.hitDot.visible = true;
    this.grazeRing.visible = true;
  }

  /** `alpha` ∈ [0,1) : fraction du tick restant à interpoler.
   *  `recoil` vient du Director : le recul est visuel et ne touche jamais la
   *  position de simulation, sinon la hitbox bougerait avec l'effet. */
  sync(w: World, alpha: number, recoil = 0): void {
    /* ── joueur ────────────────────────────────────────────────────────────
       JAMAIS interpolé (PLAN.md §3.4). L'interpolation ajoute jusqu'à un tick
       de latence visuelle, ce qui est inacceptable sur l'objet piloté — même
       si c'est plus fluide sur tous les autres. */
    /* ── ambiance et décor ────────────────────────────────────────────────
       L'étalonnage du monde est un état de la VUE, dérivé du monde courant :
       la vue est déjà le miroir de la simulation, elle est donc le bon endroit
       pour le lire. `setWorld` ne fait rien tant que le monde n'a pas changé.

       Le défilement est reconstruit depuis `w.tick` plutôt que depuis une
       horloge murale : il n'avance donc que quand la simulation avance, ce qui
       fige le décor sur tout écran de pause exactement comme le reste du jeu
       (PLAN.md §3.7 — la pause est DURE). */
    worldGrade.setWorld(worldNumberOfLevel(w.run.levelId));
    this.backdrop.update(w.tick * DT * SCROLL_SPEED);
    // Décor collisionnable, dans la même passe que le reste du monde animé.
    this.obstacles.sync(w, alpha);

    const p = w.player;
    this.player.position.set(p.x, p.y - recoil, 0);
    this.player.rotation.z = (-p.vx / FEEL.playerSpeed) * 0.42;

    /* ── SURCHAUFFE / ENRAYAGE du canon ──────────────────────────────────────
       La coque ROUGIT à mesure que la chaleur monte (rampe quadratique, discrète
       tant qu'on gère sa cadence), vire à l'incandescent PULSÉ en surchauffe
       (rouge vif, au-dessus du seuil de bloom), puis à un rouge-ORANGE qui CRÉPITE
       quand le canon s'ENRAYE — il fond, et la fumée le double. Le retour direct
       demandé : « rouge vif », puis « fumée + arrêt du tir ». */
    let er = p.heat * p.heat * 0.55;
    let og = 0.1; // part de vert → tire vers l'orange à mesure que ça chauffe
    if (p.overheated) {
      const pulse = 0.5 + 0.5 * Math.sin(w.tick * 0.35);
      er = 1.1 + 0.5 * pulse;
    }
    if (p.jammed) {
      // Enrayé : incandescence orangée qui crépite vite (le canon a fondu).
      const flick = 0.5 + 0.5 * Math.sin(w.tick * 0.9);
      er = 1.6 + 0.6 * flick;
      og = 0.24;
    }
    this.playerMat.emissive.setRGB(er, er * og, 0);

    // Clignotement d'invulnérabilité, accéléré sur la dernière demi-seconde :
    // c'est un AVERTISSEMENT de fin d'immunité, pas une décoration. Le joueur
    // doit pouvoir anticiper l'instant où il redevient mortel.
    const fast = p.iframes > 0 && p.iframes < 0.5;
    const blink = p.iframes > 0 ? (w.tick >> (fast ? 1 : 2)) % 2 === 0 : true;
    const shown = !p.dead && blink;
    this.player.visible = shown;
    this.hitDot.visible = shown;
    this.grazeRing.visible = shown;

    /* ── escorte ─────────────────────────────────────────────────────────────
       Un drone par projectile au-delà des canons de fuselage, posé EXACTEMENT
       là où `playerSystem` fait naître son tir. Aucun flottement décoratif ici :
       un drone qui oscillerait à côté de son propre projectile produirait des
       tirs sortant du vide, et le joueur cesserait de lui faire confiance.

       Ils suivent le roulis du fuselage — c'est ce qui donne à la formation
       l'air d'un appareil unique plutôt que d'un décor collé à l'écran. */
    const drones = Math.min(
      Math.max(w.progress.stats.bulletCount - HULL_GUNS, 0),
      DRONE_SLOTS.length,
    );
    let dn = 0;
    for (let i = 0; i < drones; i++) {
      /* Un drone hors service n'est PAS dessiné. C'est le retour visuel le plus
         direct possible : l'appareil disparaît, le joueur voit sa formation
         s'amputer, et il le voit revenir. Le dessiner en sombre aurait été plus
         joli et moins clair — au milieu d'un rideau, une absence se remarque,
         une nuance non. */
      if (p.droneDown[i]! > 0) continue;
      const d = DRONE_SLOTS[i]!;
      _pos.set(p.x + d.x, p.y + d.y - recoil, 0);
      _quat.setFromAxisAngle(_axisZ, this.player.rotation.z);
      _scale.setScalar(DRONE_RADIUS);
      _mat.compose(_pos, _quat, _scale);
      this.drones.setMatrixAt(dn, _mat);
      dn++;
    }
    this.drones.count = dn;
    this.drones.visible = shown && dn > 0;
    if (dn > 0) this.drones.instanceMatrix.needsUpdate = true;

    this.hitDot.position.set(p.x, p.y, 1);
    this.hitDot.scale.setScalar(11);
    this.grazeRing.position.set(p.x, p.y, 1);
    this.grazeRing.scale.setScalar(FEEL.grazeRadius);

    /* ── ennemis ─────────────────────────────────────────────────────────── */
    const en = w.enemies;
    const kinds = this.resolveArchetypes(w);
    this.fleet.begin();
    for (let i = 0; i < en.count; i++) {
      const s = en.at(i);
      const x = en.px[s]! + (en.x[s]! - en.px[s]!) * alpha;
      const y = en.py[s]! + (en.y[s]! - en.py[s]!) * alpha;

      // ⚠️ L'échelle rendue est dérivée de la HURTBOX, pas d'une constante
      // décorative. Le visuel et la boîte de collision doivent rester
      // proportionnels : un ennemi dessiné plus petit que sa hurtbox donne
      // l'impression de tirs qui touchent dans le vide, et l'inverse donne des
      // tirs qui traversent. Le rapport 1/1,2 restitue le visuel dont la
      // hurtbox vaut 120 % (PLAN.md §6.2). Les géométries étant normalisées au
      // rayon 1, l'échelle EST ce rayon — plus aucune constante ne s'intercale.
      const visual = en.r[s]! / 1.2;

      const kind = en.kind[s]!;
      const rank = w.defs.enemies[kind]?.rank ?? 0;

      let r: number, g: number, b: number;
      if (en.telegraph[s]! > 0) {
        // Télégraphe : marqueur CYAN, jamais rouge (PLAN.md §6.5). Le rouge est
        // réservé à ce qui tue ; l'employer pour annoncer produirait exactement
        // la confusion que le code couleur existe pour supprimer.
        r = 0.26;
        g = 1.5;
        b = 1.8;
      } else {
        // Un ennemi entamé s'éclaircit : information de gameplay dérivée de
        // l'état, sans un champ « flash » ajouté dans la simulation. Le
        // multiplicateur monte au-dessus de 1 — c'est du headroom HDR, il
        // pousse la coque vers le seuil de bloom et l'impact se VOIT.
        const k = en.hp[s]! / en.maxHp[s]!;
        const t = (RANK_TINT[rank] ?? 1) + (1 - k) * 0.85;
        r = t;
        g = t;
        b = t;
      }

      this.fleet.add(kinds[kind] ?? 0, x, y, visual, r, g, b);
    }
    this.fleet.end();

    /* ╔══ RENDU DES BALLES ENNEMIES — début (agent « armes exotiques ») ══════
       Chaque balle reçoit une FORME selon sa conduite (`eb.mode`), via le helper
       pur `styleFor` : orbe (défaut, INCHANGÉ), faisceau laser, disque tournant,
       missile guidé. Le joueur lit la menace dans la SILHOUETTE avant la couleur
       — vrai en vision périphérique et daltonienne (PLAN.md §4.4).

       ⚠️ Sens unique : on ne LIT que `eb.*`, jamais on n'écrit dans la sim.
       ⚠️ Zéro allocation par balle : `styleFor` renvoie des descripteurs figés,
          et l'orbe reproduit l'ancien rendu au pixel près. */
    for (const b of this.bullets) b.begin();
    const eb = w.eb;
    // Horloge de rendu dérivée du TICK (jamais murale) : elle fige en pause
    // exactement comme le décor et le clignotement joueur. Anime la rotation
    // propre du disque et le crépitement du faisceau.
    const bulletClock = w.tick * DT;
    for (let i = 0; i < eb.count; i++) {
      const s = eb.at(i);
      const fam = eb.fam[s]!;
      const batch = this.bullets[fam] ?? this.bullets[0]!;
      const x = eb.px[s]! + (eb.x[s]! - eb.px[s]!) * alpha;
      const y = eb.py[s]! + (eb.y[s]! - eb.py[s]!) * alpha;
      const vx = eb.vx[s]!;
      const vy = eb.vy[s]!;
      const sp = Math.sqrt(vx * vx + vy * vy);
      // Rayon visuel de RÉFÉRENCE, identique à l'orbe historique (r × 3,4).
      const base = eb.r[s]! * 3.4;

      const st = styleFor(eb.mode[s]!, eb.warn[s]!, eb.param[s]!);
      const size = base * st.size;
      // L'allongement par la vitesse encode la menace — mais SEULE l'orbe en
      // profite (`speedLen` = 0 sur les formes exotiques, dont la silhouette
      // porte déjà l'information).
      const len = st.len + sp * st.speedLen;

      // Orientation : rotation PROPRE du disque > orientation sur la VITESSE
      // (missile, orbe) > barre verticale FIXE (faisceau).
      let angle: number;
      if (st.spin !== 0) angle = bulletClock * st.spin;
      else if (st.follow) angle = Math.atan2(vy, vx) - Math.PI / 2;
      else angle = 0;

      // Crépitement du faisceau EN CHARGE (avertissement) : clignotement cadencé
      // sur le tick, déphasé par segment (l'indice `s`) pour un grésillement
      // électrique. Les autres formes gardent la teinte de base de leur style.
      let tint = st.tint;
      if (st.shape === BulletShape.Beam && eb.warn[s]! > 0) {
        tint *= 0.6 + 0.4 * (((w.tick + s) >> 1) & 1);
      }

      batch.add(x, y, 2, size, len, angle, st.shape, tint);
    }
    for (const b of this.bullets) b.end();
    /* ╚══ RENDU DES BALLES ENNEMIES — fin ═══════════════════════════════════ */

    /* ── projectiles joueur ──────────────────────────────────────────────── */
    const pb = w.pb;
    this.playerBullets.begin();
    this.secondaryBullets.begin();
    this.droneBullets.begin();
    /* Le RAYON secondaire (`PbMode.Beam`) est une COLONNE de segments ancrés,
       tous à l'abscisse du vaisseau. Les dessiner un par un donnait un chapelet
       d'orbes ; on les fusionne plutôt en un SEUL faisceau étiré, tracé après la
       boucle — un vrai trait de laser, pas une file de billes. */
    let beamSeen = false;
    let beamX = 0;
    let beamR = 0;
    let beamLo = Infinity;
    let beamHi = -Infinity;
    for (let i = 0; i < pb.count; i++) {
      const s = pb.at(i);
      const x = pb.px[s]! + (pb.x[s]! - pb.px[s]!) * alpha;
      const y = pb.py[s]! + (pb.y[s]! - pb.py[s]!) * alpha;
      const fam = pb.fam[s]!;

      if (pb.mode[s] === PbMode.Beam) {
        // Segment de rayon : on ne dessine rien ici, on accumule l'étendue de la
        // colonne (bas = bouche du canon, haut = tête du rayon).
        beamSeen = true;
        beamX = x;
        beamR = pb.r[s]!;
        if (y < beamLo) beamLo = y;
        if (y > beamHi) beamHi = y;
        continue;
      }

      /* ⚠️ La TAILLE vient du projectile, elle n'est plus écrite en dur.

         La version précédente dessinait tout à 15 unités quel que soit le
         rayon réel : une mine de rayon 34 s'affichait exactement comme un tir
         primaire de 14. C'est la moitié du problème d'identité visuelle des
         armes secondaires, et la moitié la plus grave — la couleur se remarque,
         mais c'est la SILHOUETTE qui fait comprendre qu'une arme est différente,
         et elle reste lisible en vision périphérique et daltonienne.

         Le rendu était en outre malhonnête : la hitbox du tir suit `pb.r`, donc
         un joueur voyait un projectile plus petit que ce qui touchait
         réellement. */
      const r = pb.r[s]!;
      // Trois silhouettes qu'on distingue au premier coup d'œil, teinte mise à
      // part : le secondaire est TRAPU, le primaire ALLONGÉ, et le drone est un
      // dard COMPACT et PLUS PETIT (multiplicateur < 1 sur un rayon déjà réduit
      // côté simulation) — un calibre d'appui, pas une copie du canon principal.
      if (fam === Fam.PlayerSecondary) this.secondaryBullets.add(x, y, 1, r * 1.35, 1.35, 0);
      else if (fam === Fam.PlayerDrone) this.droneBullets.add(x, y, 1, r * 0.95, 2.0, 0);
      else this.playerBullets.add(x, y, 1, r * 1.05, 2.8, 0);
    }

    /* Le faisceau, en UN quad : de la bouche au sommet de la colonne, forme Beam
       (noyau blanc incandescent + halo), largeur calée sur le rayon de collision
       pour que la lecture reste honnête. Un chapelet d'orbes devient un trait
       continu. On l'ajoute au lot secondaire, à sa teinte. */
    if (beamSeen) {
      const cy = (beamLo + beamHi) * 0.5;
      const width = beamR * 1.6;
      const length = beamHi - beamLo + beamR * 2;
      this.secondaryBullets.add(beamX, cy, 1, width, length / width, 0, BulletShape.Beam, 1);
    }

    this.playerBullets.end();
    this.secondaryBullets.end();
    this.droneBullets.end();

    /* ── power-ups qui tombent ─────────────────────────────────────────────
       Seuls les JETONS de bonus se dessinent (la monnaie `Cell` attend le J7).
       Ils tournent doucement et palpitent — un « viens me chercher » qui se lit
       en vision périphérique, à la teinte de leur TYPE. */
    const pu = w.pickups;
    this.pickupsView.begin();
    for (let i = 0; i < pu.count; i++) {
      const s = pu.at(i);
      const kind = pu.kind[s]!;
      if (!isPowerup(kind)) continue;
      const x = pu.px[s]! + (pu.x[s]! - pu.px[s]!) * alpha;
      const y = pu.py[s]! + (pu.y[s]! - pu.py[s]!) * alpha;
      const pulse = 0.88 + 0.12 * Math.sin(w.tick * 0.12 + s);
      const angle = w.tick * 0.03 + s;
      const c = PICKUP_RGB[kind] ?? PICKUP_RGB[PickupKind.Cell]!;
      this.pickupsView.add(x, y, PICKUP_SIZE * pulse, angle, c[0], c[1], c[2]);
    }
    this.pickupsView.end();
  }

  /**
   * Table `kind` → archétype de rendu, construite une seule fois.
   *
   * Le contenu vit dans `src/content/` et évolue sans que le rendu en soit
   * prévenu : la correspondance se fait donc par IDENTIFIANT, avec un repli par
   * rang pour tout ennemi que le catalogue ne connaît pas encore. Un ennemi
   * ajouté ailleurs s'affiche avec la silhouette de sa catégorie — faux, mais
   * jouable. C'est le seul comportement acceptable : un ennemi non dessiné
   * reste mortel, et une partie perdue face à un adversaire invisible est
   * incompréhensible pour le joueur.
   */
  private resolveArchetypes(w: World): Int32Array {
    const defs = w.defs.enemies;
    if (this.kindToArchetype && this.kindToArchetype.length === defs.length) {
      return this.kindToArchetype;
    }
    const map = new Int32Array(defs.length);
    for (let i = 0; i < defs.length; i++) {
      const d = defs[i]!;
      map[i] = archetypeFor(d.id, d.rank);
    }
    this.kindToArchetype = map;
    return map;
  }
}
